import { createRootRoute, Outlet, useRouterState, Link } from '@tanstack/react-router';
import { Toaster, toast } from 'sonner';
import { useEffect, useState } from 'react';
import { initTelegram, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { Crown, Users, Wallet, Layers, Activity, Search, Ticket, WifiOff, Lock } from 'lucide-react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persister } from '@/lib/query-client';
import { APP_VERSION } from '@/lib/version';
import { useKeyboardOpen } from '@/lib/use-keyboard-open';
import { AuthError, ApiError, TelegramUnavailableError } from '@/lib/api';

export const Route = createRootRoute({ component: RootComponent });

// Анти-спам: один 5xx-toast не чаще раза в 5с (refetch'и лупят ошибку).
let lastServerErrorToast = 0;
function notifyServerError() {
  const now = Date.now();
  if (now - lastServerErrorToast < 5000) return;
  lastServerErrorToast = now;
  toast.error('Сервер недоступен, попробуйте позже', {
    description: 'Данные временно не загружаются. Повторим автоматически.',
  });
}

function RootComponent() {
  const { t } = useTranslation();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const keyboardOpen = useKeyboardOpen();
  const [authError, setAuthError] = useState<AuthError | null>(null);
  // Telegram не отдал данные для входа (SDK не загрузился / пустая initData).
  const [tgError, setTgError] = useState<TelegramUnavailableError | null>(null);

  useEffect(() => {
    initTelegram();
    // Глобальный перехватчик auth-ошибок — JWT истёк / юзер не owner. Не показываем mock,
    // показываем явный экран. До этого фикса admin-api.ts глушил 401/403 mock-данными.
    const handleError = (error: unknown) => {
      // Данные от Telegram не пришли — до бэка запрос не дошёл, отказ доступа тут ни при чём.
      if (error instanceof TelegramUnavailableError) {
        setTgError(error);
        return;
      }
      if (error instanceof AuthError) {
        setAuthError(error);
        return;
      }
      // 5xx — бэк лёг. Не показываем нули как реальные данные молча, кидаем toast.
      if (error instanceof ApiError && error.status >= 500) {
        notifyServerError();
      }
    };
    const unsubQ = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handleError(event.action.error);
      }
    });
    const unsubM = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'error') {
        handleError(event.action.error);
      }
    });
    return () => {
      unsubQ();
      unsubM();
    };
  }, []);

  // Telegram не отдал данные для входа. Отдельный экран от «Доступ запрещён»: прав тут
  // никто не лишал, и «перезапустите WebApp» не помогает.
  if (tgError) {
    const noSdk = tgError.reason === 'no_sdk';
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background text-foreground space-y-4">
        <div className="w-16 h-16 bg-amber-500/15 text-amber-500 rounded-full flex items-center justify-center"><WifiOff className="w-7 h-7" aria-hidden /></div>
        <h2 className="text-2xl font-bold">{noSdk ? 'Telegram не отвечает' : 'Откройте админку из бота'}</h2>
        <p className="text-muted-foreground max-w-sm">
          {noSdk
            ? 'Не удалось получить данные для входа. Обычно это обрыв связи или включённый VPN. Проверьте интернет и откройте админку заново.'
            : 'Данные для входа передаёт Telegram. По прямой ссылке в браузере админка не работает: откройте её кнопкой меню в боте.'}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-primary text-primary-foreground px-6 py-3 rounded-xl text-sm font-bold"
        >
          Обновить
        </button>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background text-foreground space-y-4">
        <div className="w-16 h-16 bg-red-500/15 text-red-500 rounded-full flex items-center justify-center"><Lock className="w-7 h-7" aria-hidden /></div>
        <h2 className="text-2xl font-bold">Доступ запрещён</h2>
        <p className="text-muted-foreground max-w-sm">
          {authError.status === 403
            ? 'Этот аккаунт не имеет прав owner-кабинета.'
            : 'Сессия истекла. Перезапусти WebApp из бота.'}
        </p>
        <button
          type="button"
          onClick={() => {
            queryClient.clear();
            setAuthError(null);
          }}
          className="bg-primary text-primary-foreground px-6 py-3 rounded-xl text-sm font-bold"
        >
          Повторить
        </button>
      </div>
    );
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: persister!, maxAge: 24 * 60 * 60 * 1000, buster: APP_VERSION }}
    >
      <div
        className="min-h-screen bg-background text-foreground pb-[110px]"
        style={{
          // Вверху страницы два разных отступа, и складывать их напрямую нельзя.
          // `env(safe-area-inset-top)` это вырез экрана, но в вебвью Telegram он
          // на части устройств приходит нулём — там то же значение отдаётся
          // переменной `--tg-safe-area-inset-top`. Это ОДНО И ТО ЖЕ, поэтому
          // берём max, а не сумму: сумма даёт двойной отступ на айфоне.
          // `--tg-content-safe-area-inset-top` — своя шапка Telegram («✕ Закрыть»,
          // ⌄, ⋮), она рисуется ПОВЕРХ контента в fullscreen, и вот её нужно
          // прибавлять. Без неё верх страницы уезжает под шапку (заявка #56).
          paddingTop:
            'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)',
        }}
      >
        <div className="w-full max-w-md mx-auto">
          <Outlet />
        </div>

        <nav
          aria-hidden={keyboardOpen}
          className={`fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 p-2 rounded-full bg-black/55 [backdrop-filter:blur(20px)_saturate(160%)] [-webkit-backdrop-filter:blur(20px)_saturate(160%)] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.15)] transform-gpu [contain:paint] transition-opacity duration-150 ${
            keyboardOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
        >
          <Tab to="/" icon={<Crown size={22} />} label={t('Platform')} currentPath={currentPath} />
          <Tab to="/search" icon={<Search size={22} />} label="Поиск" currentPath={currentPath} />
          <Tab to="/partners" icon={<Users size={22} />} label={t('Partners')} currentPath={currentPath} />
          <Tab to="/finance" icon={<Wallet size={22} />} label={t('Finance')} currentPath={currentPath} />
          <Tab to="/plans" icon={<Layers size={22} />} label={t('Plans')} currentPath={currentPath} />
          <Tab to="/promos" icon={<Ticket size={22} />} label="Промо" currentPath={currentPath} />
          <Tab to="/ops" icon={<Activity size={22} />} label="Ops" currentPath={currentPath} />
        </nav>

        <Toaster
          position="bottom-center"
          theme="system"
          richColors
          offset={{ bottom: 'calc(env(safe-area-inset-bottom) + 120px)' }}
        />
      </div>
    </PersistQueryClientProvider>
  );
}

function Tab({ to, icon, label, currentPath }: { to: string; icon: React.ReactNode; label: string; currentPath: string }) {
  const isActive = to === '/' ? currentPath === '/' : currentPath === to || currentPath.startsWith(to + '/');
  // 6 пунктов в floating-pill: иконочный режим, активный — подсветка фоном (без текста,
  // иначе расширение не влезает в ширину Telegram WebApp).
  return (
    <Link
      to={to}
      preload="intent"
      // Без viewTransition: на время перехода браузер показывал застывший снимок всей
      // страницы с панелью, подсветка вкладки опаздывала.
      onClick={() => haptic('light')}
      aria-label={label}
      title={label}
      className={`flex items-center justify-center rounded-full p-2.5 transition-colors duration-100 active:scale-95 ${
        isActive
          ? 'bg-primary/20 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]'
          : 'text-white/60 active:text-white'
      }`}
    >
      {icon}
    </Link>
  );
}
